import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles, TrendingUp, Zap } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";

const BENEFITS = [
  "Pinned to the top of the browse page & category pages",
  "Neon-border highlight on your card",
  "Featured section on the homepage",
  "Verified badge visible to visitors",
  "Priority in the sitemap for faster Google indexing",
];

const HIGHLIGHT_PACKAGE = "featured_30d";

export default function Pricing() {
  const { user, loginWithDiscord } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [myServers, setMyServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState(params.get("server") || "");
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data || []));
    api.get("/featured/slots").then((r) => setSlots(r.data)).catch(() => {});
    if (user) {
      api.get("/servers/mine").then((r) => {
        setMyServers(r.data || []);
        if (!selectedServer && r.data?.length === 1) setSelectedServer(r.data[0].id);
      }).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buying more days for a server that's ALREADY featured doesn't consume a
  // new slot — server owners can always top up, so we exempt the sold-out gate
  // when the currently selected server is already featured.
  const selectedIsFeatured = myServers.find(
    (s) => s.id === selectedServer && s.is_featured,
  );
  const soldOut = !!slots?.sold_out && !selectedIsFeatured;

  const startCheckout = async (packageId) => {
    if (!user) return loginWithDiscord();
    if (!myServers.length) {
      toast.info("Submit a server first before featuring it.");
      navigate("/submit");
      return;
    }
    if (!selectedServer) {
      toast.error("Pick which server to feature");
      return;
    }
    setLoadingCheckout(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        server_id: selectedServer,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      // Handle sold-out response distinctly with a friendly toast.
      const detail = err?.response?.data?.detail;
      if (detail?.code === "featured_slots_full") {
        toast.error(detail.message || "All featured slots are taken right now.");
        // refresh slots widget so it reflects reality
        api.get("/featured/slots").then((r) => setSlots(r.data)).catch(() => {});
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Checkout failed");
      }
      setLoadingCheckout(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pt-24 pb-16 px-4 sm:px-6">
      <SEO
        title="Pricing — Feature your Discord server | Serverlistr"
        description="Pin your Discord server to the top of Serverlistr with a Featured slot. Choose from 7-day, 30-day, or 90-day packages starting at $2.99."
        path="/pricing"
      />
      <div className="max-w-5xl mx-auto">
        <Breadcrumbs items={[{ name: "Pricing" }]} />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mt-6 mb-14"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-neon-cyan mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Featured listings
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]">
            Get your Discord server{" "}
            <span className="bg-gradient-to-r from-blurple via-neon-cyan to-neon-pink bg-clip-text text-transparent">
              on top
            </span>
          </h1>
          <p className="mt-5 text-base text-zinc-400 max-w-xl mx-auto">
            Bump gets you visibility for a few hours. Featured pins you to the
            top of every relevant page for days — with a neon glow that catches
            every scroller.
          </p>
        </motion.div>

        {slots && (
          <div className="mb-8 max-w-2xl mx-auto" data-testid="featured-slots-banner">
            {slots.sold_out ? (
              <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-4 text-center">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-300">
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" /> Sold out
                </div>
                <div className="mt-2 font-heading text-lg font-black">
                  All {slots.cap} featured slots are taken right now.
                </div>
                {slots.next_open && (
                  <div className="mt-1 text-sm text-zinc-400">
                    Next slot opens {formatNextOpen(slots.next_open)}.
                  </div>
                )}
                <div className="mt-2 text-xs text-zinc-500">
                  Already featured? You can still top up your existing slot below.
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-4 text-center">
                <div className="text-sm text-zinc-300">
                  <span className="font-heading text-lg font-black text-neon-cyan">{slots.available}</span>
                  <span className="text-zinc-500"> of {slots.cap} featured slots available</span>
                </div>
                {slots.available <= 3 && (
                  <div className="mt-1 text-[11px] text-yellow-400 font-semibold uppercase tracking-widest">
                    ⚡ Almost sold out
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {user && myServers.length > 1 && (
          <div className="mb-8 max-w-md mx-auto rounded-2xl border border-white/10 bg-[#0A0A0F] p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
              Which server to feature?
            </div>
            <select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              data-testid="pricing-server-select"
              className="w-full bg-[#12121A] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-blurple focus:outline-none"
            >
              <option value="">Pick a server…</option>
              {myServers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-grid">
          {packages.map((pkg) => {
            const highlight = pkg.id === HIGHLIGHT_PACKAGE;
            const perDay = (pkg.amount / pkg.days).toFixed(2);
            return (
              <div
                key={pkg.id}
                data-testid={`pricing-card-${pkg.id}`}
                className={`relative rounded-2xl p-6 flex flex-col ${
                  highlight
                    ? "border-2 border-neon-cyan/60 bg-gradient-to-b from-neon-cyan/10 to-transparent shadow-[0_0_40px_-10px_rgba(34,211,238,0.4)]"
                    : "border border-white/10 bg-[#0A0A0F]"
                }`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-neon-cyan text-black text-[10px] font-black uppercase tracking-widest">
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-2 text-neon-cyan">
                  {pkg.days <= 7 ? <Zap className="w-4 h-4" /> : pkg.days >= 90 ? <TrendingUp className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  <span className="font-heading text-lg font-bold">{pkg.name || `${pkg.days} days`}</span>
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-5xl font-black">${pkg.amount}</span>
                  <span className="text-sm text-zinc-500">/ {pkg.days}d</span>
                </div>
                <div className="mt-1 text-xs font-mono text-zinc-500">
                  ${perDay}/day
                </div>

                <ul className="mt-6 space-y-2.5 text-sm text-zinc-300 flex-1">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-neon-green flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => startCheckout(pkg.id)}
                  disabled={loadingCheckout === pkg.id || soldOut}
                  data-testid={`checkout-${pkg.id}`}
                  className={`mt-7 h-11 rounded-xl font-bold transition-all active:scale-95 inline-flex items-center justify-center gap-2 ${
                    soldOut
                      ? "bg-white/5 text-zinc-500 cursor-not-allowed"
                      : highlight
                      ? "bg-neon-cyan text-black hover:bg-neon-cyan/90"
                      : "bg-blurple hover:bg-blurple-hover text-white"
                  } disabled:opacity-60`}
                >
                  {soldOut
                    ? "Sold out"
                    : loadingCheckout === pkg.id
                    ? "Redirecting…"
                    : user
                    ? (selectedIsFeatured ? "Extend feature" : "Feature my server")
                    : "Sign in to feature"}
                  {!soldOut && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 max-w-2xl mx-auto text-center space-y-3 text-sm text-zinc-500">
          <p>
            Payments processed securely via <span className="text-white">Stripe</span>.
            One-time charge — no subscriptions, no auto-renew.
          </p>
          <p>
            Featured slots stack — buying another package extends your current
            window instead of replacing it.
          </p>
          <p>
            Questions? Ping us in our{" "}
            <a href="https://discord.gg/HWQGPR6nR4" target="_blank" rel="noreferrer" className="text-blurple hover:underline">
              Discord server
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function formatNextOpen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  if (diffMs <= 0) return "soon";
  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  if (days >= 1) return `in ~${days}d ${hours}h`;
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  if (hours >= 1) return `in ~${hours}h ${minutes}m`;
  return `in ~${minutes}m`;
}
